import { useQuery } from "@tanstack/react-query";
import { Loader2, Building2, Target, TrendingUp, Users2 } from "lucide-react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";

const AXIS = { fontSize: 11, fill: "#94a3b8" };
const TIP = { fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" };

function Kpi({ label, value, icon: Icon, testid }) {
  return (
    <Card className="p-4 shadow-none border-slate-200/80" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-slate-400">
        <Icon className="h-3.5 w-3.5" />
        <p className="text-[11px] font-semibold uppercase tracking-wider">{label}</p>
      </div>
      <p className="font-heading text-2xl font-bold text-slate-900 mt-1">{value}</p>
    </Card>
  );
}

export default function DealerBI() {
  const { data, isLoading } = useQuery({
    queryKey: ["mis-analytics"],
    queryFn: async () => (await api.get("/sales-mis/analytics")).data,
  });
  if (isLoading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  const plans = data?.dealer_plans || [];
  const master = data?.dealer_master || [];
  const funnel = data?.dealer_funnel || [];
  const totalPlan = plans.reduce((a, p) => a + (p.plan || 0), 0);
  const totalActual = plans.reduce((a, p) => a + (p.actual || 0), 0);
  const funnelValue = funnel.reduce((a, f) => a + (f.value_lakhs || 0), 0);
  const top = [...plans].sort((a, b) => (b.plan || 0) - (a.plan || 0)).slice(0, 10);

  return (
    <div data-testid="dealer-bi-page">
      <PageHeader title="Dealer BI — Dashboard" subtitle="Dealer performance from the live Sales MIS — plans, achievement and pipeline." />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <Kpi label="Dealers (plan)" value={plans.length || master.length} icon={Building2} testid="dbi-kpi-dealers" />
        <Kpi label="Annual Plan" value={`${totalPlan.toFixed(1)}`} icon={Target} testid="dbi-kpi-plan" />
        <Kpi label="Achieved" value={`${totalActual.toFixed(2)}`} icon={TrendingUp} testid="dbi-kpi-achieved" />
        <Kpi label="Funnel Value" value={`${(funnelValue / 100).toFixed(1)}`} icon={Users2} testid="dbi-kpi-funnel" />
      </div>

      <Card className="p-4 mb-5 shadow-none border-slate-200/80" data-testid="dbi-plan-chart">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-3">Top 10 Dealers — Plan vs Achieved (INR Cr)</p>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={top} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis dataKey="dealer" tick={{ ...AXIS, fontSize: 10 }} axisLine={false} tickLine={false} interval={0} angle={-20} height={50} textAnchor="end" />
            <YAxis tick={{ ...AXIS, fontSize: 10 }} axisLine={false} tickLine={false} width={36} />
            <Tooltip contentStyle={TIP} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="plan" name="Plan" fill="#334155" radius={[3, 3, 0, 0]} maxBarSize={26} />
            <Bar dataKey="actual" name="Achieved" fill="#a3231b" radius={[3, 3, 0, 0]} maxBarSize={26} />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      <div className="grid lg:grid-cols-2 gap-5">
        <Card className="p-4 shadow-none border-slate-200/80 overflow-hidden" data-testid="dbi-plans-table">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Dealer Plans ({plans.length})</p>
          <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-white">
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                  <th className="py-1.5 pr-2">Dealer</th><th className="pr-2">Zone</th>
                  <th className="pr-2 text-right">Plan (Cr)</th><th className="pr-2 text-right">Achieved</th>
                  <th className="pr-2 text-right">Opps</th><th className="text-right">Leads</th>
                </tr>
              </thead>
              <tbody>
                {[...plans].sort((a, b) => (b.plan || 0) - (a.plan || 0)).map((p, i) => (
                  <tr key={i} className="border-b border-slate-50">
                    <td className="py-1.5 pr-2 font-medium text-slate-800">{p.dealer}</td>
                    <td className="pr-2 text-slate-500">{p.zone || "—"}</td>
                    <td className="pr-2 text-right font-mono">{(p.plan || 0).toFixed(1)}</td>
                    <td className="pr-2 text-right font-mono">{(p.actual || 0).toFixed(2)}</td>
                    <td className="pr-2 text-right">{Math.round(p.opp_act || 0)}/{Math.round(p.opp_plan || 0)}</td>
                    <td className="text-right">{Math.round(p.leads_act || 0)}/{Math.round(p.leads_plan || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
        <Card className="p-4 shadow-none border-slate-200/80 overflow-hidden" data-testid="dbi-funnel-table">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Dealer Funnel ({funnel.length} records)</p>
          <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-white">
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                  <th className="py-1.5 pr-2">Project</th><th className="pr-2">Dealer</th>
                  <th className="pr-2">Kind</th><th className="pr-2 text-right">Value (L)</th><th>EDC</th>
                </tr>
              </thead>
              <tbody>
                {[...funnel].sort((a, b) => (b.value_lakhs || 0) - (a.value_lakhs || 0)).map((f, i) => (
                  <tr key={i} className="border-b border-slate-50">
                    <td className="py-1.5 pr-2 font-medium text-slate-800">{f.project}</td>
                    <td className="pr-2 text-slate-500">{f.dealer || "—"}</td>
                    <td className="pr-2">{f.kind || "—"}</td>
                    <td className="pr-2 text-right font-mono">{(f.value_lakhs || 0).toFixed(1)}</td>
                    <td className="text-slate-500">{f.edc || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}
